import React, { useState, useEffect, useCallback } from "react";
import { fetchPosts, createPost, replyToPost } from "@/lib/api";
import { useAuth, startGoogleLogin } from "@/lib/auth";
import { useLocale } from "@/lib/locale";
import { Button } from "@/components/ui/button";
import { MessageCircle, AlertTriangle, EyeOff, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import UserBadge from "./UserBadge";

function SpoilerBody({ body, spoiler, t }) {
  const [revealed, setRevealed] = useState(false);
  if (!spoiler) return <p className="text-zinc-200 font-body whitespace-pre-wrap break-words">{body}</p>;
  if (!revealed) {
    return (
      <button
        onClick={() => setRevealed(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-red-400/30 bg-red-500/10 text-red-300 text-xs font-heading"
        data-testid="spoiler-reveal"
      >
        <EyeOff className="h-3 w-3" /> {t("spoiler_warning")}
      </button>
    );
  }
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.22em] text-red-300/80 mb-1 flex items-center gap-1">
        <AlertTriangle className="h-3 w-3" /> spoiler
      </div>
      <p className="text-zinc-200 font-body whitespace-pre-wrap break-words">{body}</p>
    </div>
  );
}

function PostItem({ post, onReply, t, user }) {
  const [showReply, setShowReply] = useState(false);
  const [text, setText] = useState("");
  const [spoiler, setSpoiler] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await onReply(post.id, text, spoiler);
      setText(""); setSpoiler(false); setShowReply(false);
    } finally { setBusy(false); }
  };

  return (
    <li className="border border-white/10 rounded-md bg-obsidian-surface/60 p-5" data-testid={`post-${post.id}`}>
      <div className="flex items-start gap-3 mb-3">
        {post.user_picture ? (
          <img src={post.user_picture} alt="" className="h-9 w-9 rounded-full object-cover border border-white/10" />
        ) : (
          <div className="h-9 w-9 rounded-full bg-amber/20 text-amber flex items-center justify-center font-heading">
            {post.user_name?.[0] || "F"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link
              to={user && user.user_id !== post.user_id ? `/u/${post.user_id}` : "#"}
              className="font-heading text-sm text-white hover:text-amber"
            >
              {post.user_name}
            </Link>
            <UserBadge user={post} />
            <span className="text-zinc-600 text-xs">·</span>
            <span className="text-zinc-500 text-xs font-body">{new Date(post.created_at).toLocaleDateString()}</span>
          </div>
          <div className="mt-2">
            <SpoilerBody body={post.body} spoiler={post.spoiler} t={t} />
          </div>
        </div>
      </div>

      {(post.replies || []).length > 0 && (
        <ul className="ml-12 mt-3 space-y-2 border-l border-white/10 pl-4">
          {post.replies.map((rep) => (
            <li key={rep.id} className="text-sm" data-testid={`reply-${rep.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-amber font-heading text-xs">{rep.user_name}</span>
                <span className="text-zinc-600 text-xs">·</span>
                <span className="text-zinc-500 text-xs">{new Date(rep.created_at).toLocaleDateString()}</span>
              </div>
              <SpoilerBody body={rep.body} spoiler={rep.spoiler} t={t} />
            </li>
          ))}
        </ul>
      )}

      <div className="ml-12 mt-3">
        {!showReply ? (
          <button
            onClick={() => setShowReply(true)}
            className="text-xs text-zinc-500 hover:text-amber font-heading"
            data-testid={`reply-btn-${post.id}`}
          >
            ↳ {t("reply")}
          </button>
        ) : (
          <div className="space-y-2">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("write_reply")}
              rows={2}
              className="w-full bg-black/30 border border-white/10 rounded-md p-2 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none focus:border-amber/50"
              data-testid={`reply-input-${post.id}`}
            />
            <div className="flex items-center justify-between">
              <label className="inline-flex items-center gap-2 text-xs text-zinc-500 font-heading">
                <input type="checkbox" checked={spoiler} onChange={(e) => setSpoiler(e.target.checked)} />
                {t("mark_spoiler")}
              </label>
              <Button
                size="sm" disabled={busy}
                className="bg-amber hover:bg-amber-hover text-obsidian font-heading"
                onClick={submit}
                data-testid={`reply-submit-${post.id}`}
              >
                {t("reply")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </li>
  );
}

export default function FanBoard({ titleId }) {
  const { t } = useLocale();
  const { user } = useAuth() || {};
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [spoiler, setSpoiler] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetchPosts(titleId).then(setPosts).finally(() => setLoading(false));
  }, [titleId]);
  useEffect(load, [load]);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await createPost(titleId, text, spoiler);
      setText(""); setSpoiler(false);
      load();
      toast.success("Posted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to post");
    } finally { setBusy(false); }
  };

  const handleReply = async (postId, body, sp) => {
    await replyToPost(postId, body, sp);
    load();
  };

  return (
    <section data-testid="fan-board">
      <div className="text-[11px] uppercase tracking-[0.25em] text-amber font-heading mb-1 flex items-center gap-1.5">
        <MessageCircle className="h-3 w-3" /> {t("fan_board")}
      </div>
      <h2 className="font-display text-3xl text-white mb-2">{t("fan_board_sub")}</h2>

      {user ? (
        <div className="mt-4 mb-6 glass rounded-md p-4 space-y-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("write_post")}
            rows={3}
            className="w-full bg-black/30 border border-white/10 rounded-md p-3 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none focus:border-amber/50"
            data-testid="post-input"
          />
          <div className="flex items-center justify-between">
            <label className="inline-flex items-center gap-2 text-xs text-zinc-500 font-heading">
              <input type="checkbox" checked={spoiler} onChange={(e) => setSpoiler(e.target.checked)} data-testid="post-spoiler" />
              {t("mark_spoiler")}
            </label>
            <Button
              disabled={busy || !text.trim()}
              onClick={submit}
              className="bg-amber hover:bg-amber-hover text-obsidian font-heading"
              data-testid="post-submit"
            >
              {t("post_btn")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 mb-6 border border-white/10 rounded-md p-5 text-center bg-obsidian-surface/60">
          <p className="text-zinc-400 font-body mb-3">{t("sign_in_to_post")}</p>
          <Button onClick={startGoogleLogin} className="bg-amber hover:bg-amber-hover text-obsidian font-heading" data-testid="board-signin-btn">
            {t("sign_in")}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-amber" /></div>
      ) : (
        <ul className="space-y-3" data-testid="posts-list">
          {posts.length === 0 ? (
            <li className="text-zinc-500 font-body italic text-sm">Be the first to start a conversation.</li>
          ) : (
            posts.map((p) => <PostItem key={p.id} post={p} onReply={handleReply} t={t} user={user} />)
          )}
        </ul>
      )}
    </section>
  );
}
